<template>
        <div>
            <h3 v-if="!this.isAddEquipmentMode" class="editor-header px-2">
                {{ $t('culvert.culvert')  }}
            </h3>
            <location-deviation v-if="!this.isAddEquipmentMode" :location-deviation="item.location_deviation"/>
            <marker-editor-map
                v-if="item && !this.isAddEquipmentMode"
                :id="item.id"
                class="item-map"
                :lat="parseFloat(item.position.y)"
                :lon="parseFloat(item.position.x)"
                :type="this.CULVERT"
                :draggable="false"
            />

            <div class="nopads">
                <div v-if="!this.isAddEquipmentMode" class="px-2">
                    <div class="col-sm-6">
                        <span class="span-title">{{ $t('common.id') }}</span>
                        <span class="item-detail-text">{{ item.external_system_id }}</span>
                    </div>
                    <div class="col-sm-6">
                        <road-addess-view :road-number="item.road_num" :section-number="item.road_sec" :distance="item.distance" :show-street-name="false"/>
                    </div>
                </div>
                <div class="row px-2">
                    <div class="col-sm-6">
                        <label class="form-label small fw-bold">{{ $t('culvert.material') }}</label>
                        <select
                            v-model="item.material"
                            class="form-select form-select-sm"
                        >
                            <option
                                v-for="option in culvertMaterials"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.text }}
                            </option>
                        </select>
                    </div>

                    <div class="col-sm-6 nopads px-1">
                        <label class="form-label small fw-bold">{{ $t('culvert.type') }}</label>
                        <select
                            v-model="item.type"
                            class="form-select form-select-sm"
                            :class="{ 'is-valid': !!item.type, 'is-invalid': item.type === null }"
                        >
                            <option
                                v-for="option in culvertTypes"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                </div>
                <location-details
                    class="ps-3 pe-2"
                    v-if="!this.isAddEquipmentMode"
                    :lane-data="item.location_specification.lane"
                    :side-data="item.location_specification.side"
                    :track-data="item.location_specification.tracks"
                    />
                <div class="row px-2">
                    <div class="col-sm-6">
                        <label class="form-label small fw-bold">{{ $t('culvert.size') }}</label>
                        <input
                            v-model="item.size"
                            type="number"
                            min="0"
                            class="form-control form-control-sm"
                        />
                    </div>

                    <div class="col-sm-6 nopads px-1">
                        <label class="form-label small fw-bold">{{ $t('culvert.length') }}</label>
                        <input
                            v-model="item.length"
                            type="number"
                            min="0"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-6 nopads ps-3 pr-1">
                        <condition-selector v-model="item.condition" :required="true"/>
                    </div>
                </div>
                <div class="row" v-if="this.isAddEquipmentMode">
                    <div class="col-sm-12 nopads mt-2">
                        <h4 class="form-header nopads mt-0">{{ $t('velho_equipment.location_spesifications') }}</h4>
                        <velho-equipment-location-details-editor
                        :hideRampSelector="true"
                        :hideShoulderSelector="true"
                        :hidePlatformSelector="true"
                        :hideEdgeAreaSelector="true"
                        :hideDitchBottomSelector="true"
                        :hideDividerSelector="true"
                        :hideCenterAreaSelector="true"
                        :roadInfo="item"
                        :locationSpecification="item.location_specification"
                        />
                    </div>
                </div>
                <hr class="mb-0">
                <div class="row" v-if="this.isAddEquipmentMode">
                    <div class="col-sm-6 nopads mt-2">
                        <equipment-date
                            :text="$t('velho_equipment.start_date')"
                            v-model="item.created_at"
                        />
                    </div>
                </div>
                <div class="row px-1">
                    <div class="col-sm-12 mt-2">
                        <velho-equipment-damages :items="item.equipment_damages" :isPercentageVisible="true"/>
                    </div>
                </div>
                <div class="row px-1">
                    <equipment-actions v-if="!this.isAddEquipmentMode" ref="actions" v-model="item.actions" />
                    <linked-equipment
                        v-if="item.linked_equipment && item.linked_equipment.length > 0"
                        :linked-equipments="item.linked_equipment"
                    />
                </div>
            </div>
            <velho-editor-buttons
                v-if="!this.isAddEquipmentMode"
                :url="extCulvertsUrl"
                :selected-contract="selectedContract"
                :item="item"
                :disabled="loading"
                @close="close"
                @submit="handleSubmit"
                @removalConfirmed="removalConfirmed"
            />
        <div
            v-if="loading > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import MarkerEditorMap from '../map/MarkerEditorMap'
import {mapHelper} from "@/components/mixins/MapMixin";
import {conversionModelHelper, material} from "@/components/mixins/ConversionMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import ConditionSelector from "@/components/velho/ConditionSelector";
import {velhoHelper} from "@/components/mixins/VelhoMixin";
import EquipmentActions from "@/components/velho/EquipmentActions";
import VelhoEditorButtons from "@/components/velho/VelhoEditorButtons";
import {roadSignHelper} from "@/components/mixins/RoadSignMixin";
import LinkedEquipment from "@/components/velho/LinkedEquipment";
import LocationDetails from "@/components/velho/LocationDetails.vue";
import LocationDeviation from "@/components/velho/LocationDeviation";
import VelhoEquipmentLocationDetailsEditor from "@/components/velho/VelhoEquipmentLocationDetailsEditor";
import VelhoEquipmentDamages from "@/components/velho/VelhoEquipmentDamages";
import RoadAddessView from "@/components/velho/RoadAddressView";
import {velhoValidationMixin} from "@/components/mixins/VelhoValidationMixin";
import EquipmentDate from "@/components/velho/EquipmentDate.vue";

export default {
    name: 'CulvertEditor',
    mixins: [velhoHelper, mapHelper, conversionModelHelper, restApi, roadSignHelper, velhoValidationMixin],
    components: {
        EquipmentDate,
        RoadAddessView,
        LocationDeviation, VelhoEquipmentLocationDetailsEditor, VelhoEquipmentDamages,
        VelhoEditorButtons, EquipmentActions, ConditionSelector, MarkerEditorMap, LocationDetails, LinkedEquipment},
    props: {
        item: {
            type: Object,
            default: null
        },
        selectedContract: {
            type: Object,
            default: null
        },
        setInspectionParams: {
            type: Function,
            default: null
        },
        addEquipmentEditor: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isAddEquipmentMode() {
            return this.addEquipmentMode
        }
    },
    data() {
        return {
            addEquipmentMode: this.addEquipmentEditor,
            map: null,
            loading: false,
            culvertMaterials: [
                { value: null, text: this.$t('culvert.select_material')},
                { value: material.CONCRETE, text: this.getMaterialString(material.CONCRETE)},
                { value: material.METAL, text: this.getMaterialString(material.METAL)},
                { value: material.PLASTIC, text: this.getMaterialString(material.PLASTIC)},
                { value: material.OTHER, text: this.getMaterialString(material.OTHER)},
            ],
            culvertTypes: [
                { value: null, text: this.$t('culvert.select_type')},
                { value: 1, text: this.$t('culvert.type_highway')},
                { value: 2, text: this.$t('culvert.type_street')},
                { value: 3, text: this.$t('culvert.type_private_road')},
                { value: 4, text: this.$t('culvert.type_side_ditch')},
                { value: 5, text: this.$t('culvert.type_animal')},
            ]
        }
    },
    methods: {
        handleSubmit: function(url, item, selectedContract, replace = false) {
            if(replace) {
                this.submitEquipmentsreplace(this.extEquipmentsReplaceUrl, [item], selectedContract)
            } else {
                this.submitEquipment(url, item, selectedContract);
            }
        },
        close: function () {
            this.$emit('close')
        },
        changeView: function () {
            if (this.setInspectionParams) {
                this.setInspectionParams('inspectionWizard', 'culvert', this.item);
            }
            this.$emit('close')
        },
        isValidForm: function() {
            if(!this.item) { return false }
            if(!this.validateRoadNumber(this.item.road_num)) { return false }
            if(!this.validateRoadSection(this.item.road_sec)) { return false }
            if(!this.validateRoadDistance(this.item.distance)) { return false }
            if(!this.validateRoadSide(this.item.location_specification.side)) { return false }
            if(!this.validateRoadTracks(this.item.location_specification.tracks)) { return false }
            if(!this.validateIsNumber(this.item.condition)) { return false }

            return true
        }
    }
}
</script>

<style scoped>
.map-container {
    height: 30em;
    position: relative;
}

</style>
